import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { useTranslation } from "react-i18next"
import { ArrowLeft, ArrowUpRight, Info } from "lucide-react"
import { useStore } from "@/store"
import useToast from "@/hooks/useToast"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import { validateUserFields } from "@/lib/userValidation"
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { FetchStatus } from "@/hooks/useFetch"
import {
  type ClientUserAgentInteraction,
  type ClientUserMemoryEntry,
  type ClientUserDetail,
  useGetClientUserAgentInteractionsApi,
  useGetClientUserApi,
  useGetClientUserMetricsApi,
  useGetWorkspaceApi,
  useUpdateClientUserAgentInteractionApi,
  useUpdateClientUserApi,
  workspaceResponseToWorkspace,
} from "@/hooks/api"
import { OrganizationRole, WorkspaceRole } from "@/types"

const UserDetailPage = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { workspaceId = "", userId = "" } = useParams()

  const [currentOrganization] = useStore((s) => [s.currentOrganization])
  const organizationId = currentOrganization?.id || ""

  const { data: workspaceResp, fetchFn: getWorkspace } = useGetWorkspaceApi(
    organizationId,
    workspaceId
  )
  const workspace = useMemo(() => {
    if (!workspaceResp?.data) return undefined
    return workspaceResponseToWorkspace(workspaceResp.data)
  }, [workspaceResp])
  const isIdentityMasked = !!workspace?.maskEndUserIdentity

  const canEdit = useMemo(() => {
    if (!workspace) return false
    const isOrgPrivileged =
      currentOrganization?.myRole === OrganizationRole.Owner ||
      currentOrganization?.myRole === OrganizationRole.Admin
    return (
      isOrgPrivileged ||
      workspace.myRole === WorkspaceRole.Owner ||
      workspace.myRole === WorkspaceRole.Admin
    )
  }, [workspace, currentOrganization])

  const {
    data: userResp,
    status: userStatus,
    fetchFn: getUser,
  } = useGetClientUserApi(organizationId, workspaceId, userId)
  const { fetchFn: updateUser } = useUpdateClientUserApi(
    organizationId,
    workspaceId,
    userId
  )
  const { fetchFn: updateAgentInteraction } =
    useUpdateClientUserAgentInteractionApi(organizationId, workspaceId, userId)
  const { status: metricsStatus, fetchFn: getMetrics } =
    useGetClientUserMetricsApi(organizationId, workspaceId, userId)
  const {
    data: agentInteractionsResp,
    status: agentInteractionsStatus,
    fetchFn: getAgentInteractions,
  } = useGetClientUserAgentInteractionsApi(organizationId, workspaceId, userId)

  const user: ClientUserDetail | undefined = userResp?.data

  const [nameDraft, setNameDraft] = useState("")
  const [aliasDraft, setAliasDraft] = useState("")
  const [emailDraft, setEmailDraft] = useState("")
  const [phoneDraft, setPhoneDraft] = useState("")
  const [companyDraft, setCompanyDraft] = useState("")
  const [jobTitleDraft, setJobTitleDraft] = useState("")
  const [statusDraft, setStatusDraft] = useState("active")
  const [notesDraft, setNotesDraft] = useState("")
  const [memoryEntriesDraft, setMemoryEntriesDraft] = useState<
    ClientUserMemoryEntry[]
  >([])
  const [isSaving, setIsSaving] = useState(false)
  const [interactionUpdatingAgentId, setInteractionUpdatingAgentId] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (!organizationId || !workspaceId) return
    getWorkspace()
  }, [organizationId, workspaceId, getWorkspace])

  useEffect(() => {
    if (!organizationId || !workspaceId || !userId) return
    getUser()
    getMetrics({ searchParams: new URLSearchParams({ range: "30" }) })
    getAgentInteractions()
  }, [
    organizationId,
    workspaceId,
    userId,
    getUser,
    getMetrics,
    getAgentInteractions,
  ])

  useEffect(() => {
    if (!user) return
    setNameDraft(user.name || "")
    setAliasDraft(user.alias || "")
    setEmailDraft(user.email || "")
    setPhoneDraft(user.phone || "")
    setCompanyDraft(user.company || "")
    setJobTitleDraft(user.job_title || "")
    setStatusDraft(user.status || "active")
    setNotesDraft(user.provider_notes || "")
    setMemoryEntriesDraft(user.memory_entries || [])
  }, [user])

  const agentInteractions: ClientUserAgentInteraction[] = useMemo(
    () => agentInteractionsResp?.data || [],
    [agentInteractionsResp]
  )

  const fieldValidation = useMemo(
    () =>
      validateUserFields({
        email: emailDraft,
        phone: phoneDraft,
      }),
    [emailDraft, phoneDraft]
  )

  const hasChanges = useMemo(() => {
    if (!user) return false
    const baseStatus = user.status || "active"
    if (isIdentityMasked) return statusDraft !== baseStatus

    const memoryDraft = JSON.stringify(memoryEntriesDraft)
    const memoryOriginal = JSON.stringify(user.memory_entries || [])
    return (
      nameDraft !== (user.name || "") ||
      aliasDraft !== (user.alias || "") ||
      emailDraft !== (user.email || "") ||
      phoneDraft !== (user.phone || "") ||
      companyDraft !== (user.company || "") ||
      jobTitleDraft !== (user.job_title || "") ||
      statusDraft !== baseStatus ||
      notesDraft !== (user.provider_notes || "") ||
      memoryDraft !== memoryOriginal
    )
  }, [
    user,
    isIdentityMasked,
    nameDraft,
    aliasDraft,
    emailDraft,
    phoneDraft,
    companyDraft,
    jobTitleDraft,
    statusDraft,
    notesDraft,
    memoryEntriesDraft,
  ])

  const handleAddMemoryEntry = () => {
    setMemoryEntriesDraft((prev) => [
      ...prev,
      {
        content: "",
        source: "admin",
      },
    ])
  }

  const handleUpdateMemoryEntry = (index: number, value: string) => {
    setMemoryEntriesDraft((prev) =>
      prev.map((entry, idx) =>
        idx === index ? { ...entry, content: value } : entry
      )
    )
  }

  const handleRemoveMemoryEntry = (index: number) => {
    setMemoryEntriesDraft((prev) => prev.filter((_, idx) => idx !== index))
  }

  const handleSave = async () => {
    if (!canEdit) return
    if (!hasChanges) return
    if (
      !isIdentityMasked &&
      (!fieldValidation.isEmailValid || !fieldValidation.isPhoneValid)
    )
      return
    const trimmedMemoryEntries = memoryEntriesDraft.map((entry) => ({
      ...entry,
      content: entry.content?.trim() || "",
    }))
    if (
      !isIdentityMasked &&
      trimmedMemoryEntries.some((entry) => !entry.content)
    ) {
      toast({
        title: t("crm.userManagement.memory.emptyError"),
        variant: "destructive",
      })
      return
    }
    try {
      setIsSaving(true)
      await updateUser({
        jsonBody: isIdentityMasked
          ? { status: statusDraft }
          : {
              name: nameDraft,
              alias: aliasDraft,
              email: emailDraft,
              phone: phoneDraft,
              company: companyDraft,
              job_title: jobTitleDraft,
              status: statusDraft,
              provider_notes: notesDraft,
              memory_entries: trimmedMemoryEntries,
            },
      })
      toast({ title: t("crm.userManagement.messages.userUpdated") })
      getUser()
    } catch (error) {
      toast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("crm.userManagement.errors.updateUser"),
          fallbackDescription: "",
        }),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    navigate(`/workspaces/${workspaceId}?tab=user-management`)
  }

  const userBadgeVariant = (s: string) => {
    if (s === "blocked") return "destructive"
    return "secondary"
  }

  const userBadgeClassName = (s: string) => {
    if (s === "active")
      return "bg-green-600 text-white hover:bg-green-700 border-transparent"
    return ""
  }

  const buildHistoriesPath = (agentId?: string) => {
    const pem = (user?.external_user_id || "").trim()
    const params = new URLSearchParams()
    params.set("tab", "histories")
    params.set("wid", workspaceId)
    params.set("page", "1")
    if (pem && !isIdentityMasked) params.set("pem", pem)
    if (user?.id) params.set("cuid", user.id)
    if (agentId) params.set("aid", agentId)
    return `/workspaces/${workspaceId}?${params.toString()}`
  }

  const handleViewInHistories = () => {
    navigate(buildHistoriesPath())
  }

  const getInteractionModeLabel = (mode?: string | null) => {
    switch (mode) {
      case "human":
        return t("crm.userManagement.interactionMode.options.human")
      case "ai":
      default:
        return t("crm.userManagement.interactionMode.options.ai")
    }
  }

  const getAutoHandoffLabel = (enabled: boolean) =>
    enabled
      ? t("crm.userManagement.detail.autoHandoffEnabled")
      : t("crm.userManagement.detail.autoHandoffDisabled")

  const getInteractionModeSourceLabel = (source?: string | null) => {
    switch (source) {
      case "agent_handoff":
        return t("crm.userManagement.interactionMode.sources.agentHandoff")
      case "agent_policy":
        return t("crm.userManagement.interactionMode.sources.agentPolicy")
      case "crm_manual":
        return t("crm.userManagement.interactionMode.sources.crmManual")
      case "auto_timeout":
        return t("crm.userManagement.interactionMode.sources.autoTimeout")
      default:
        return "-"
    }
  }

  const formatTimestamp = (value?: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString()
  }

  const handleUpdateAgentInteraction = async (
    agentId: string,
    nextMode: "ai" | "human"
  ) => {
    if (!canEdit) return
    try {
      setInteractionUpdatingAgentId(agentId)
      await updateAgentInteraction({
        jsonBody: { interaction_mode: nextMode },
        urlBuilderParams: { agentId },
      })
      toast({
        title: t("crm.userManagement.messages.interactionModeUpdated"),
      })
      await Promise.all([getUser(), getAgentInteractions()])
    } catch (error) {
      toast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("crm.userManagement.errors.updateInteractionMode"),
          fallbackDescription: "",
        }),
      })
    } finally {
      setInteractionUpdatingAgentId(null)
    }
  }

  if (!organizationId || !workspaceId || !userId) return null

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="space-y-1">
            <div className="text-lg font-semibold">
              {(isIdentityMasked
                ? user?.display_name || user?.external_user_id
                : user?.alias || user?.display_name) ||
                t("crm.userManagement.detail.title")}
            </div>
            <div className="text-sm text-green-600">
              {user?.external_user_id || userId}
            </div>
          </div>
        </div>
        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={
              !hasChanges ||
              isSaving ||
              (!isIdentityMasked &&
                (!fieldValidation.isEmailValid ||
                  !fieldValidation.isPhoneValid))
            }
          >
            {t("crm.userManagement.detail.save")}
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">
              {t("crm.userManagement.detail.basicInfo")}
            </div>
            {user?.status && (
              <Badge
                variant={userBadgeVariant(user.status)}
                className={userBadgeClassName(user.status)}
              >
                {user.status === "active"
                  ? t("crm.userManagement.filters.active")
                  : t("crm.userManagement.filters.blocked")}
              </Badge>
            )}
          </div>

          {userStatus === FetchStatus.Error && (
            <div className="text-sm text-red-600">
              {t("crm.userManagement.errors.loadUserDetail")}
            </div>
          )}

          {isIdentityMasked && (
            <div className="text-sm text-muted-foreground">
              {t("crm.userManagement.detail.identityMaskedNotice")}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {!isIdentityMasked && (
              <>
                <div className="space-y-2">
                  <Label>{t("crm.userManagement.detail.name")}</Label>
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("crm.userManagement.detail.alias")}</Label>
                  <Input
                    value={aliasDraft}
                    onChange={(e) => setAliasDraft(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("crm.userManagement.detail.email")}</Label>
                  <Input
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    disabled={!canEdit}
                    className={
                      fieldValidation.isEmailValid ? "" : "border-red-500"
                    }
                  />
                  {!fieldValidation.isEmailValid && (
                    <div className="text-sm text-red-500">
                      {t("crm.userManagement.addUser.emailInvalid")}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("crm.userManagement.detail.externalUserId")}</Label>
              <Input value={user?.external_user_id || ""} disabled />
            </div>
            {!isIdentityMasked && (
              <>
                <div className="space-y-2">
                  <Label>{t("crm.userManagement.addUser.phone")}</Label>
                  <Input
                    value={phoneDraft}
                    onChange={(e) => setPhoneDraft(e.target.value)}
                    disabled={!canEdit}
                    className={
                      fieldValidation.isPhoneValid ? "" : "border-red-500"
                    }
                  />
                  {!fieldValidation.isPhoneValid && (
                    <div className="text-sm text-red-500">
                      {t("crm.userManagement.addUser.phoneInvalid")}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("crm.userManagement.addUser.company")}</Label>
                  <Input
                    value={companyDraft}
                    onChange={(e) => setCompanyDraft(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>{t("crm.userManagement.addUser.jobTitle")}</Label>
                  <Input
                    value={jobTitleDraft}
                    onChange={(e) => setJobTitleDraft(e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>{t("crm.userManagement.detail.status")}</Label>
              <Select
                value={statusDraft}
                onValueChange={setStatusDraft}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    {t("crm.userManagement.filters.active")}
                  </SelectItem>
                  <SelectItem value="blocked">
                    {t("crm.userManagement.filters.blocked")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("crm.userManagement.detail.joinSource")}</Label>
              <Input value={user?.join_source || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>{t("crm.userManagement.detail.joinedAt")}</Label>
              <Input value={user?.joined_at || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>{t("crm.userManagement.detail.updatedAt")}</Label>
              <Input value={user?.updated_at || ""} disabled />
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-lg border p-4">
          <div className="text-base font-semibold">
            {t("crm.userManagement.detail.usageStats")}
          </div>
          {metricsStatus === FetchStatus.Error && (
            <div className="text-sm text-red-600">
              {t("crm.userManagement.errors.loadMetrics")}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                {t("crm.userManagement.detail.lastLoginAt")}
              </div>
              <div className="text-sm">{user?.last_login_at || "-"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                {t("crm.userManagement.detail.lastConversationAt")}
              </div>
              <div className="text-sm">{user?.last_conversation_at || "-"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                {t("crm.userManagement.detail.conversationCount")}
              </div>
              <div className="text-sm">{user?.conversation_count ?? 0}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                {t("crm.userManagement.detail.createdAt")}
              </div>
              <div className="text-sm">{user?.created_at || "-"}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-base font-semibold">
            {t("crm.userManagement.detail.agentRouting")}
          </div>
          <div className="max-w-xl text-sm text-muted-foreground sm:text-right">
            {t("crm.userManagement.detail.agentRoutingHelp")}
          </div>
        </div>
        {agentInteractionsStatus === FetchStatus.Error && (
          <div className="text-sm text-red-600">
            {t("crm.userManagement.errors.loadAgentInteractions")}
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("crm.userManagement.detail.agent")}</TableHead>
              <TableHead>
                {t("crm.userManagement.detail.conversationCount")}
              </TableHead>
              <TableHead>
                {t("crm.userManagement.detail.lastConversationAt")}
              </TableHead>
              <TableHead>
                {t("crm.userManagement.detail.interactionMode")}
              </TableHead>
              <TableHead>
                {t("crm.userManagement.detail.autoHandoff")}
              </TableHead>
              <TableHead>
                {t("crm.userManagement.detail.interactionModeSource")}
              </TableHead>
              <TableHead>
                {t("crm.userManagement.detail.humanModeExpiresAt")}
              </TableHead>
              <TableHead className="w-[190px] text-right">
                {t("crm.userManagement.detail.action")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agentInteractions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-sm text-muted-foreground"
                >
                  {t("crm.userManagement.detail.noAgentRouting")}
                </TableCell>
              </TableRow>
            ) : (
              agentInteractions.map((interaction) => {
                const canEnterHuman = interaction.is_published
                const isUpdating =
                  interactionUpdatingAgentId === interaction.agent_id
                const isHuman = interaction.interaction_mode === "human"
                const isManualHuman =
                  isHuman &&
                  interaction.interaction_mode_source === "crm_manual"
                const isPolicyControlled =
                  interaction.agent_response_mode === "human_only"
                return (
                  <TableRow key={interaction.agent_id}>
                    <TableCell className="font-medium">
                      {interaction.agent_name || interaction.agent_id}
                    </TableCell>
                    <TableCell>{interaction.conversation_count}</TableCell>
                    <TableCell>
                      {formatTimestamp(interaction.last_conversation_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge
                          variant={isHuman ? "outline" : "secondary"}
                          className={
                            isHuman
                              ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                              : ""
                          }
                        >
                          {getInteractionModeLabel(
                            interaction.interaction_mode
                          )}
                        </Badge>
                        {isPolicyControlled ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                aria-label={t(
                                  "crm.userManagement.detail.agentPolicyHumanHelp"
                                )}
                              >
                                <Info className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-xs whitespace-normal text-left"
                            >
                              {t(
                                "crm.userManagement.detail.agentPolicyHumanHelp"
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ) : isManualHuman ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                aria-label={t(
                                  "crm.userManagement.detail.manualHumanPersistentHelp"
                                )}
                              >
                                <Info className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-xs whitespace-normal text-left"
                            >
                              {t(
                                "crm.userManagement.detail.manualHumanPersistentHelp"
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {getAutoHandoffLabel(interaction.handoff_enabled)}
                    </TableCell>
                    <TableCell>
                      {getInteractionModeSourceLabel(
                        interaction.interaction_mode_source
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(interaction.human_mode_expires_at)}
                    </TableCell>
                    <TableCell className="w-[190px]">
                      <div className="flex flex-nowrap items-center justify-end gap-1.5">
                        {canEdit ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 whitespace-nowrap px-2.5 text-xs"
                            disabled={
                              isUpdating ||
                              (isHuman && isPolicyControlled) ||
                              (!isHuman && !canEnterHuman)
                            }
                            onClick={() =>
                              handleUpdateAgentInteraction(
                                interaction.agent_id,
                                isHuman ? "ai" : "human"
                              )
                            }
                          >
                            {isHuman
                              ? t("crm.userManagement.detail.switchToAi")
                              : t("crm.userManagement.detail.switchToHuman")}
                          </Button>
                        ) : null}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              asChild
                            >
                              <Link
                                to={buildHistoriesPath(interaction.agent_id)}
                                aria-label={t(
                                  "crm.userManagement.detail.viewAgentHistories"
                                )}
                              >
                                <ArrowUpRight className="h-4 w-4" />
                              </Link>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {t("crm.userManagement.detail.viewAgentHistories")}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {canEdit && !isIdentityMasked && (
        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">
              {t("crm.userManagement.detail.providerNotes")}
            </div>
          </div>
          <Textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder={t("crm.userManagement.detail.notesPlaceholder")}
            className="min-h-[140px]"
          />
        </div>
      )}

      {canEdit && !isIdentityMasked && (
        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">
              {t("crm.userManagement.table.memory")}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddMemoryEntry}
              disabled={isSaving}
            >
              {t("crm.userManagement.memory.addEntry")}
            </Button>
          </div>
          {memoryEntriesDraft.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {t("crm.userManagement.memory.empty")}
            </div>
          ) : (
            <div className="space-y-3">
              {memoryEntriesDraft.map((entry, index) => (
                <div
                  key={entry.id || `memory-${index}`}
                  className="space-y-2 rounded-md border p-3"
                >
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div>
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleString()
                        : t("crm.userManagement.memory.newEntry")}
                      {entry.source ? ` • ${entry.source}` : ""}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMemoryEntry(index)}
                      disabled={isSaving}
                    >
                      {t("crm.userManagement.memory.removeEntry")}
                    </Button>
                  </div>
                  <Textarea
                    value={entry.content}
                    onChange={(e) =>
                      handleUpdateMemoryEntry(index, e.target.value)
                    }
                    placeholder={t(
                      "crm.userManagement.memory.entryPlaceholder"
                    )}
                    rows={3}
                    className="text-sm"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-base font-semibold">
            {t("crm.userManagement.detail.conversationHistory")}
          </div>
          <Button variant="outline" onClick={handleViewInHistories}>
            <ArrowUpRight className="mr-2 h-4 w-4" />
            {t("crm.userManagement.detail.viewInHistories")}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default UserDetailPage
